<script setup lang="ts">
import { computed, nextTick, onMounted, type Ref, ref } from 'vue';
import { RobotVisualizer } from '@therobotcompany/robot-visualizer';
import cloneDeep from 'lodash.clonedeep';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { useRoute } from 'vue-router';
import formatDate from '@/helpers/DateFormatterHelper';
import { EMPTY_ROBOT_DETAILS, type RobotDetails } from '@/models/RobotDetails';
import router from '@/router';
import HealthIndicatorEnum from '@/enums/HealthIndicatorEnum';
import { requiredRule } from '@/helpers/FormRules';
import {
  healthColor,
  healthIndicatorText,
} from '@/helpers/HealthIndicatorHelper';
import type { RobotCompositionModule } from '@/models/RobotCompositionModule';
import type { RobotModule } from '@/models/Robot';
import useAuthStore from '@/store/AuthStore';
import useBreadcrumbStore from '@/store/BreadcrumbStore';
import gettext from '@/i18n/gettext';
import i18nKeys from '@/helpers/I18nHelper';
import OpenInRobflowButton from '@/components/atoms/OpenInRobflowButton.vue';
import { DEFAULT_DISPLAY_ANGLES, MODULES_BASE_URL } from '@/Constants';
import { loadRobotDetails, patchRobotName } from '@/api/RobotsApi';
import loadRobotRobFlowVariables from '@/api/RobFlowApi';
import RobFlowVariablesDialog from '@/components/robot/RobFlowVariablesDialog.vue';
import type RobFlowVariables from '@/models/RobFlow';
import { Permission } from '@/models/Roles';
import RobotFinderApi from '@/api/RobotFinderApi';

const { $gettext } = gettext;
const authStore = useAuthStore();
const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();
const breadcrumbStore = useBreadcrumbStore();
const robotSerialNumber: Ref<string> = ref('');

const robotDetails: Ref<RobotDetails> = ref(cloneDeep(EMPTY_ROBOT_DETAILS));
const error = ref('');
const robotLoaded = ref(false);
const editName = ref(false);
const editNameField = ref();

const displayPose: Ref<number[]> = ref([]);

const variablesDialog = ref(false);
const robflowVariables: Ref<RobFlowVariables | null> = ref(null);

const openVariablesDialog = () => {
  variablesDialog.value = true;
};

async function loadRobot(): Promise<boolean> {
  try {
    const newRobotDetails: RobotDetails = await loadRobotDetails(
      robotSerialNumber.value,
    );
    if (!newRobotDetails.serialNumber) {
      error.value = $gettext(i18nKeys.noRobotDataAvailable);
    }
    const modulesInOrder: RobotModule[] = newRobotDetails.robotComposition.map(
      (compositionModule: RobotCompositionModule) => {
        if (
          newRobotDetails.modules.filter(
            (module: RobotModule) =>
              module.moduleSerialNumber ===
              compositionModule.moduleSerialNumber,
          ).length > 0
        ) {
          return newRobotDetails.modules.filter(
            (module: RobotModule) =>
              module.moduleSerialNumber ===
              compositionModule.moduleSerialNumber,
          )[0];
        }

        // Fallback: module data are not found.
        // Add module with fallback data anyway to ensure the list is complete
        return {
          moduleType: compositionModule.moduleType,
          moduleTypeId: compositionModule.moduleTypeId,
          moduleSerialNumber: compositionModule.moduleSerialNumber,
          healthIndicator: HealthIndicatorEnum.HEALTHY,
          healthIndicatorDetails: {
            status: HealthIndicatorEnum.HEALTHY,
            warning: [],
            error: [],
          },
        };
      },
    );

    robotDetails.value = newRobotDetails;
    robotDetails.value.modules = modulesInOrder;

    robflowVariables.value = await loadRobotRobFlowVariables(
      robotDetails.value.serialNumber,
    );

    const moduleFolderMapping = await RobotFinderApi.getModuleFolderMapping();

    for (let i = 0; i < robotDetails.value.modules.length; i += 1) {
      const module = robotDetails.value.modules[i];
      if (moduleFolderMapping[module.moduleTypeId]) {
        robotDetails.value.modules[i].moduleType = moduleFolderMapping[
          module.moduleTypeId
        ].moduleName.replace('-KM', '');
      }
      if (!robotDetails.value.modules[i].moduleType) {
        robotDetails.value.modules[i].moduleType = $gettext(
          'Generic endeffector',
        );
      }
    }

    // Load display pose for current robot (for visualization)
    const relevantModuleIds = robotDetails.value.modules
      .filter(
        (module) =>
          module.moduleType?.startsWith('BD') ||
          module.moduleType?.startsWith('D') ||
          module.moduleType?.startsWith('I') ||
          module.moduleType?.startsWith('L'),
      )
      .map((module) => module.moduleTypeId)
      .join(',');

    const displayPoseResponse =
      await RobotFinderApi.getDisplayPose(relevantModuleIds);

    if (displayPoseResponse) {
      displayPose.value = displayPoseResponse;
    } else {
      displayPose.value = DEFAULT_DISPLAY_ANGLES;
    }

    // filter out clamps, so they don't show up in the module list
    robotDetails.value.modules = robotDetails.value.modules.filter(
      (mod: RobotModule) => !mod.moduleTypeId?.startsWith('8'),
    );

    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
    router.push('not-found');
    return false;
  } finally {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function getHealthColor(healthIndicator: HealthIndicatorEnum) {
  return healthColor(healthIndicator);
}

function getHealthString(healthIndicator: HealthIndicatorEnum) {
  return healthIndicatorText(healthIndicator);
}

function doFormatDate(date: string) {
  return formatDate(date, true);
}

const moduleIds = computed(() => {
  return robotDetails.value.robotComposition
    .map((robotModule) => {
      if (
        parseInt(robotModule.moduleTypeId, 10) >= 9900 &&
        parseInt(robotModule.moduleTypeId, 10) <= 9999
      ) {
        // Generic Endeffector. Render Module with ID 0906 (E-gimatic-sz25-koch)
        robotModule.moduleTypeId = '0906';
      }
      return robotModule;
    })
    .map((robotModule) => robotModule.moduleTypeId);
});

async function saveRobotName() {
  try {
    editName.value = false;
    await patchRobotName(robotDetails.value.name, robotDetails.value.uuid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    error.value = err;
    console.error(err);
  }
}

function enableEdit() {
  editName.value = true;
  nextTick(() => {
    editNameField.value.focus();
  });
}

onMounted(async () => {
  if (route.query.serial_number) {
    robotSerialNumber.value = route.query.serial_number.toString();
  }

  // no control unit selected for details -> route back to fleet overview
  if (robotSerialNumber.value === '') {
    router.push('/fleet-management');
    return;
  }
  breadcrumbStore.setDynamicItems([{ title: robotSerialNumber.value }]);
  loadingSpinnerStore.setShowLoadingOverlay(true);
  robotLoaded.value = await loadRobot();
  loadingSpinnerStore.setShowLoadingOverlay(false);
  if (robotDetails.value.name) {
    breadcrumbStore.setDynamicItems([{ title: robotDetails.value.name }]);
  }
});
</script>
<template>
  <div v-if="robotLoaded" class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Details for') }}
          <span class="font-weight-medium">{{ robotDetails.name }}</span>
        </h1>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12" md="8" lg="6" xl="4">
        <v-alert
          v-if="error"
          class="mt-8"
          border="top"
          icon="mdi-close-circle"
          border-color="error"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-sheet class="h-100" elevation="2" rounded="xl">
          <div class="status-badges">
            <v-tooltip anchor="bottom" location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  class="mr-2"
                  :color="robotDetails.onlineState ? 'success' : 'error'"
                  icon="mdi-satellite-uplink"
                  v-bind="props"
                />
              </template>
              <span v-if="robotDetails.onlineState">
                {{ $gettext('Online') }}
              </span>
              <span v-if="!robotDetails.onlineState">
                {{ $gettext('Last seen') }}:
                {{ doFormatDate(robotDetails.lastSeen) }}
              </span>
            </v-tooltip>
            <v-tooltip anchor="bottom" location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  class="mr-2"
                  icon="mdi-alarm-light"
                  v-bind="props"
                  :color="getHealthColor(robotDetails.overallHealthIndicator)"
                />
              </template>
              <span>
                {{ $gettext('Control Unit health status') }}:
                {{
                  $gettext(getHealthString(robotDetails.overallHealthIndicator))
                }}
              </span>
            </v-tooltip>
          </div>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4" lg="3" xl="2">
                <h4>
                  {{ $gettext('Robot') }}
                </h4>
                <div v-if="!editName" class="mt-1">
                  <h1>
                    {{ robotDetails.name }}
                  </h1>
                </div>
                <div v-else class="mt-3">
                  <v-text-field
                    ref="editNameField"
                    v-model="robotDetails.name"
                    class="textfield-compact mt-6"
                    density="compact"
                    label="Name"
                    variant="underlined"
                    :rules="[requiredRule]"
                    v-on:keyup.enter="
                      robotDetails.name === '' ? undefined : saveRobotName()
                    "
                    v-on:keyup.esc="editName = false"
                    ><template #append-inner>
                      <v-icon
                        :icon="
                          robotDetails.name === ''
                            ? 'mdi-close'
                            : 'mdi-check-bold'
                        "
                        :color="robotDetails.name === '' ? 'error' : 'success'"
                        :disabled="robotDetails.name === ''"
                        @click="
                          robotDetails.name === '' ? undefined : saveRobotName()
                        "
                      />
                    </template>
                  </v-text-field>
                </div>

                <br />
                <div class="text--primary">
                  {{ $gettext('Serial number') }}:
                  <b>{{ robotDetails.serialNumber }}</b>
                </div>

                <div class="text--primary">
                  {{ $gettext('Online status') }}:
                  <b>{{ robotDetails.onlineState ? 'online' : 'offline' }}</b>
                </div>

                <div class="text--primary">
                  {{ $gettext('Overall health status') }}:
                  <b>{{
                    $gettext(
                      getHealthString(robotDetails.overallHealthIndicator),
                    )
                  }}</b>
                </div>

                <br /><br />
                <div class="text--primary">
                  {{ $gettext('Robot Controller Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robcoRobotControllerVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Robflow Frontend Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robflowFrontendVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Robflow Backend Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robflowBackendVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Module Database Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robcoModuleDatabaseVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Robot Communication Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robcoRobotCommunicationVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Telemetry Version') }}:
                  <b>{{
                    robotDetails.softwareVersions.robcoTelemetryVersion
                  }}</b>
                </div>
                <div class="text--primary">
                  {{ $gettext('Utils Version') }}:
                  <b>{{ robotDetails.softwareVersions.robflowUtilsVersion }}</b>
                </div>
              </v-col>
              <v-col cols="12" md="8" lg="9" xl="10">
                <div
                  class="position-relative mt-8 mb-2"
                  style="height: 278px; border-radius: 16px"
                >
                  <robot-visualizer
                    :modules-base-url="MODULES_BASE_URL"
                    :current-angles="displayPose"
                    :module-ids="moduleIds"
                    background-color="#fff"
                    loading-spinner-color="#D7DEE9"
                    :show-axes-helper="false"
                  ></robot-visualizer>
                  <div
                    style="
                      top: 8px;
                      right: 8px;
                      z-index: 2051;
                      position: absolute;
                    "
                  >
                    <v-btn
                      size="small"
                      icon="mdi-arrow-expand"
                      :href="`/visualizer?modules=${moduleIds}&angles=${displayPose}`"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <open-in-robflow-button
              :control-unit-ip="robotDetails.ipAddress"
              :is-online="robotDetails.onlineState === true"
            />
            <r-btn
              class="ml-2"
              variant="outlined"
              :disabled="editName"
              @click="enableEdit"
            >
              {{ $gettext('Edit') }}
            </r-btn>
            <v-spacer />
            <r-btn
              v-if="robflowVariables"
              variant="outlined"
              @click="openVariablesDialog"
            >
              {{ $gettext('RobFlow Variables') }}
            </r-btn>
            <r-btn
              v-if="authStore.hasPermission(Permission.GET_GRAFANA)"
              class="mx-2"
              variant="outlined"
              :href="robotDetails.grafanaLink"
            >
              {{ $gettext('Stats') }}
            </r-btn>
          </v-card-actions>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="(module, index) in robotDetails.modules"
        :key="index"
        cols="12"
        md="6"
        lg="4"
        xl="3"
        xxl="2"
      >
        <v-card class="h-100">
          <div class="status-badges">
            <v-tooltip anchor="bottom" location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  class="mr-2"
                  icon="mdi-alarm-light"
                  v-bind="props"
                  :color="getHealthColor(module.healthIndicator)"
                />
              </template>
              <span>
                {{ $gettext('Module health status') }}:
                {{ $gettext(getHealthString(module.healthIndicator)) }}
              </span>
            </v-tooltip>
          </div>
          <v-card-text>
            <div class="font-weight-bold">{{ $gettext('Module') }}</div>
            <h1 class="mt-1">
              {{ module.moduleType ? module.moduleType : 'Unknown' }}
            </h1>
            <br />
            <div class="text--primary">
              {{ $gettext('Serial number') }}:
              <b>{{ module.moduleSerialNumber }}</b>
            </div>
            <div class="text--primary">
              {{ $gettext('Module health status') }}:
              <b>{{ $gettext(getHealthString(module.healthIndicator)) }}</b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <RobFlowVariablesDialog
      v-model:isOpen="variablesDialog"
      :robflowVariables="robflowVariables"
    />
  </div>
</template>
